/**
 * Collection drop pill-bar filter engine (chips + value pills + fold-down
 * panel / bottom sheet + sort), wired per plans/20260820-horizon-rebuild/
 * phase-08-collection-drop.md. Plain IIFE + delegated listeners, matching
 * every other afroyla-*.js module in this theme (afroyla-coupon-strip.js,
 * afroyla-search-results.js) rather than a Shadow-DOM web component — the
 * phase-06 PDP risk notes already document why this theme avoids Shadow DOM
 * for anything that needs simple cross-script coordination.
 *
 * ---- DEGRADED-MODE CONTRACT (read sections/afroyla-collection-drop.liquid's
 * top comment first) ----
 * Two of the three filter axes (form, ships) may not yet be recognized by
 * Shopify's own facet engine — `data-sd-form-configured` / `data-sd-ships-
 * configured` on the root say which are. This module ALWAYS renders the
 * correct visible set instantly from the JSON data island
 * (afroyla-drop-data-island.liquid), regardless of configuration state — that
 * part never needs the network. `needsFetch(state, prev, ctx)` decides
 * whether a transition also needs a real Section Rendering round trip (same
 * diff-based shape as assets/afroyla-collection-hub.js's needsFetch — mirror,
 * not fork): it fires on any price/sort change, and on a form/ships change
 * ONLY once that axis is `data-sd-*-configured`. A fetch fired while an
 * UNCONFIGURED axis is active still returns a valid (if wider) result set —
 * Shopify silently ignored the param it doesn't recognize, so the swapped-in
 * grid/island is a superset of the true match; the render() pass that follows
 * every swap re-applies the full client-side `matches()` check and hides
 * whatever the fetch couldn't filter, so the visible result stays correct
 * either way (fail open, never fail closed).
 */
(function () {
  'use strict';

  if (window.__aflDropFilterInit) return;
  window.__aflDropFilterInit = true;

  var ROOT_SEL = '[data-afl-collection-drop]';
  var PRICE_KEYS = ['under50', '100_149', '150plus'];
  var PRICE_LABELS = { under50: 'Under $50', '100_149': '$100–149', '150plus': '$150+' };
  var SHIPS_KEYS = ['now', 'preorder'];

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  /* ---- state <-> URL ------------------------------------------------------ */

  function stateFromSearch(search) {
    var p = new URLSearchParams(search);
    var lte = p.get('filter.v.price.lte');
    var gte = p.get('filter.v.price.gte');
    var price = '';
    if (lte && !gte && parseFloat(lte) <= 50) price = 'under50';
    else if (gte && lte && parseFloat(gte) >= 100 && parseFloat(lte) < 150) price = '100_149';
    else if (gte && !lte && parseFloat(gte) >= 150) price = '150plus';
    return {
      form: p.get('filter.p.m.custom.form_group') || '',
      price: price,
      ships: p.get('filter.p.m.custom.ships_status') || '',
      sort: p.get('sort_by') || ''
    };
  }

  function urlForState(collectionUrl, state) {
    var parts = [];
    if (state.form) parts.push('filter.p.m.custom.form_group=' + encodeURIComponent(state.form));
    if (state.price === 'under50') parts.push('filter.v.price.lte=50');
    else if (state.price === '100_149') parts.push('filter.v.price.gte=100&filter.v.price.lte=149.99');
    else if (state.price === '150plus') parts.push('filter.v.price.gte=150');
    if (state.ships) parts.push('filter.p.m.custom.ships_status=' + encodeURIComponent(state.ships));
    if (state.sort) parts.push('sort_by=' + encodeURIComponent(state.sort));
    return parts.length ? collectionUrl + '?' + parts.join('&') : collectionUrl;
  }

  // Diff-based on purpose (H1/M3 review fix) — mirrors
  // assets/afroyla-collection-hub.js's needsFetch(state, prev, ctx) exactly,
  // so both commit() and the popstate handler below share one rule: a real
  // Section Rendering round trip fires when price/sort changed (always
  // server-real), or when a CONFIGURED axis's value changed. This also fixes
  // the "Back after any fetch shows a stale narrowed grid" bug — popstate
  // now runs the same check against the state it's leaving, so restoring an
  // earlier (already-fetched-and-swapped-narrower) DOM back to a wider state
  // always re-fetches instead of just re-toggling `hidden` on whatever few
  // card nodes happen to still be in the DOM.
  function needsFetch(state, prev, ctx) {
    if (state.price !== prev.price || state.sort !== prev.sort) return true;
    if (ctx.sdForm && state.form !== prev.form) return true;
    if (ctx.sdShips && state.ships !== prev.ships) return true;
    return false;
  }

  /* ---- island matching ----------------------------------------------------- */

  function matches(item, state) {
    if (state.form && item.form !== state.form) return false;
    if (state.price === 'under50' && !(item.price < 50)) return false;
    if (state.price === '100_149' && !(item.price >= 100 && item.price < 150)) return false;
    if (state.price === '150plus' && !(item.price >= 150)) return false;
    if (state.ships === 'now' && item.now !== true) return false;
    if (state.ships === 'preorder' && item.now !== false) return false;
    return true;
  }

  function countFor(items, state) {
    var n = 0;
    for (var i = 0; i < items.length; i++) if (matches(items[i], state)) n++;
    return n;
  }

  /* ---- render (idempotent — safe to call on mount, after a client filter,
     or after a section-fetch swap) -------------------------------------- */

  function render(ctx, state) {
    var visible = {};
    for (var i = 0; i < ctx.items.length; i++) visible[ctx.items[i].handle] = matches(ctx.items[i], state);
    var count = countFor(ctx.items, state);
    var hasFilter = !!(state.form || state.price || state.ships);

    qsa(ctx.root, '[data-afl-card]').forEach(function (cell) {
      var handle = cell.getAttribute('data-handle');
      var known = Object.prototype.hasOwnProperty.call(visible, handle);
      // Cards beyond the island's 50-item cap can't be evaluated client-side —
      // fail open (never hide a card we have no data for) rather than lie.
      cell.hidden = known ? !visible[handle] : false;
    });

    qsa(ctx.root, '[data-afl-chip]').forEach(function (chip) {
      var active = (chip.getAttribute('data-form-value') || '') === state.form;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-current', active ? 'true' : 'false');
    });

    syncPill(ctx, 'price', state.price, PRICE_LABELS[state.price], state);
    syncPill(
      ctx,
      'ships',
      state.ships,
      state.ships === 'now' ? 'Ships now' : state.ships === 'preorder' ? 'Pre-order — ' + ctx.window : '',
      state
    );

    var resultEl = qs(ctx.root, '[data-afl-result]');
    var resultText = qs(ctx.root, '[data-afl-result-text]');
    var emptyEl = qs(ctx.root, '[data-afl-empty]');
    var tailEl = qs(ctx.root, '[data-afl-tail]');
    if (resultEl) resultEl.hidden = !hasFilter;
    if (resultText) resultText.textContent = count + ' pieces ·';
    if (emptyEl) emptyEl.hidden = !(hasFilter && count === 0);
    // data-page1 can arrive empty from a Section Rendering swap — treat only an
    // explicit "false" (page > 1) as not-page-1, so the tail fails open on page 1.
    if (tailEl) tailEl.hidden = hasFilter || tailEl.getAttribute('data-page1') === 'false';

    syncPanelRows(ctx);
  }

  /* Apply-label preview state: the sim's own idle default previews the FIRST
     price row (Under $50) rather than "no price restriction" — "Show 3
     pieces", not "Show {count(form only)}". Used only for the Apply button's
     live math now (M1 review fix removed the deferred/pending panel step —
     see the row-click handler below), never to decide what's actually
     applied. */
  function applyLabelPreview(state) {
    return Object.assign({}, state, { price: state.price || 'under50' });
  }

  /* Row "checked" ink-fill reflects the REAL committed state (ctx.state) —
     rows apply immediately on click (M1), there is nothing pending to
     preview here anymore. */
  function syncPanelRows(ctx) {
    qsa(ctx.root, '[data-afl-panel-row]').forEach(function (row) {
      var axis = row.getAttribute('data-axis');
      var idx = parseInt(row.getAttribute('data-value'), 10);
      var key = axis === 'price' ? PRICE_KEYS[idx] : SHIPS_KEYS[idx];
      row.classList.toggle('is-active', ctx.state[axis] === key);
    });
    syncApplyLabel(ctx, applyLabelPreview(ctx.state));
  }

  function syncPill(ctx, axis, value, label, state) {
    var existing = qs(ctx.root, '[data-afl-pill="' + axis + '"]');
    if (!value) {
      if (existing) existing.remove();
      return;
    }
    if (!existing) {
      existing = document.createElement('a');
      existing.className = 'afl-drop-bar__pill';
      existing.setAttribute('data-afl-pill', axis);
      var cluster = qs(ctx.root, '.afl-drop-bar__cluster');
      if (cluster) cluster.insertBefore(existing, cluster.firstChild);
    }
    var next = state;
    next = Object.assign({}, state);
    next[axis] = '';
    existing.href = urlForState(ctx.collectionUrl, next);
    existing.textContent = '';
    var textNode = document.createTextNode(label + ' ');
    var x = document.createElement('span');
    x.className = 'afl-drop-bar__pill-x';
    x.setAttribute('aria-hidden', 'true');
    x.textContent = '✕';
    existing.appendChild(textNode);
    existing.appendChild(x);
  }

  function syncApplyLabel(ctx, previewState) {
    var n = countFor(ctx.items, previewState);
    qsa(ctx.root, '[data-afl-panel-apply]').forEach(function (btn) {
      btn.textContent = 'Show ' + n + (n === 1 ? ' piece' : ' pieces');
      btn.href = urlForState(ctx.collectionUrl, previewState);
    });
  }

  /* ---- fetch: Section Rendering round trip + region swap ------------------ */

  /* M3 review fix: a sequence token on ctx so a slow/out-of-order response
     (e.g. a fast double-click, or a popstate landing while a commit's fetch
     is still in flight) can never swap a stale grid over a newer one — only
     the MOST RECENTLY issued fetch's response is allowed to touch the DOM. */
  function fetchAndSwap(ctx, state) {
    var mySeq = ++ctx.fetchSeq;
    var url = urlForState(ctx.collectionUrl, state);
    var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(ctx.sectionId);
    fetch(fetchUrl, { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html || mySeq !== ctx.fetchSeq) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshRoot = doc.querySelector(ROOT_SEL);
        if (!freshRoot) return;
        ['[data-afl-grid]', '[data-afl-result]', '[data-afl-empty]', '[data-afl-tail]', '.pagination'].forEach(function (sel) {
          var freshNode = freshRoot.querySelector(sel);
          var liveNode = ctx.root.querySelector(sel);
          if (freshNode && liveNode) liveNode.replaceWith(freshNode);
          else if (!freshNode && liveNode && sel === '.pagination') liveNode.remove();
        });
        // Re-run the local pass against the CURRENT state (ctx.state), not
        // this closure's `state` — a newer commit/popstate may have moved on
        // while the request was in flight (M3 review fix); the sequence-token
        // check above already guarantees this is still the latest request.
        render(ctx, ctx.state);
      })
      .catch(function () {
        /* Fetch failed — the optimistic client render from above already
           stands as the final state; nothing to roll back to. */
      });
  }

  /* ---- commit: pushState + client render + (maybe) section fetch --------- */

  function commit(ctx, state, replaceHistory) {
    var prev = ctx.state;
    ctx.state = state;
    var url = urlForState(ctx.collectionUrl, state);
    var method = replaceHistory ? 'replaceState' : 'pushState';
    history[method]({ afroylaDrop: state }, '', url);
    render(ctx, state);
    document.dispatchEvent(new CustomEvent('filter:update', { detail: { state: state, count: countFor(ctx.items, state) } }));

    if (needsFetch(state, prev, ctx)) fetchAndSwap(ctx, state);
  }

  /* ---- panel open/close, scroll lock, focus trap -------------------------- */

  function openPanel(ctx) {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var panel = qs(ctx.root, isMobile ? '[data-afl-panel="mobile"]' : '[data-afl-panel="desktop"]');
    if (!panel) return;
    panel.hidden = false;
    // Rows always reflect ctx.state (M1 — no deferred/pending step anymore);
    // render() already keeps them in sync on every commit, nothing to
    // recompute just for opening.
    qsa(ctx.root, '[data-afl-filter-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });
    if (isMobile) {
      document.body.classList.add('afl-drop-scroll-lock');
      var closeBtn = panel.querySelector('[data-afl-filter-close]');
      if (closeBtn) closeBtn.focus();
    }
  }

  function closePanel(ctx) {
    qsa(ctx.root, '[data-afl-panel]').forEach(function (panel) {
      panel.hidden = true;
    });
    qsa(ctx.root, '[data-afl-filter-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('afl-drop-scroll-lock');
  }

  function isPanelOpen(ctx) {
    return qsa(ctx.root, '[data-afl-panel]').some(function (p) {
      return !p.hidden;
    });
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

  /* Mobile sheet only — the desktop fold-down is in-flow (no overlay), so
     Tab naturally can't "escape" it the way a fixed dialog needs guarding. */
  function trapFocus(ctx, event) {
    if (event.key !== 'Tab') return;
    var sheet = qs(ctx.root, '[data-afl-panel="mobile"]');
    if (!sheet || sheet.hidden) return;
    var focusable = qsa(sheet, FOCUSABLE);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---- init ----------------------------------------------------------------- */

  function init(root) {
    if (root.__afroylaDrop) return;
    root.__afroylaDrop = true;

    var islandEl = root.querySelector('[data-afl-drop-island]');
    var items = [];
    try {
      items = islandEl ? JSON.parse(islandEl.textContent) || [] : [];
    } catch (e) {
      items = [];
    }

    var ctx = {
      root: root,
      items: items,
      collectionUrl: root.getAttribute('data-collection-url'),
      sectionId: root.getAttribute('data-section-id'),
      sdForm: root.getAttribute('data-sd-form-configured') === 'true',
      sdShips: root.getAttribute('data-sd-ships-configured') === 'true',
      window: root.getAttribute('data-window') || '',
      state: null,
      fetchSeq: 0
    };

    var initial = stateFromSearch(location.search);
    ctx.state = initial;
    render(ctx, initial);

    // Chips: immediate commit (single-select, closes any open panel).
    qsa(root, '[data-afl-chip]').forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        closePanel(ctx);
        var next = Object.assign({}, ctx.state, { form: chip.getAttribute('data-form-value') || '' });
        commit(ctx, next);
      });
    });

    // Filter+ toggle / close.
    qsa(root, '[data-afl-filter-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        isPanelOpen(ctx) ? closePanel(ctx) : openPanel(ctx);
      });
    });
    qsa(root, '[data-afl-filter-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        closePanel(ctx);
      });
    });
    var backdrop = qs(root, '[data-afl-sheet-backdrop]');
    if (backdrop) backdrop.addEventListener('click', function () { closePanel(ctx); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isPanelOpen(ctx)) closePanel(ctx);
      trapFocus(ctx, e);
    });

    // Panel rows (M1 review ruling — "y chang như mockup"): apply AND close
    // immediately, mirroring the sim's `applyUnder` handler
    // (scratchpad/pw/build-collection-filter-wired.mjs — `panelCols` is
    // reused byte-for-byte by both the desktop fold-down and the mobile
    // sheet, and `onClick="{{ applyUnder }}"` is the SAME handler on the row
    // AND the Apply button; the source has no breakpoint split). This also
    // matches each row's own plain `<a href>` no-JS behavior, which already
    // navigates immediately — the previous deferred/pending step was a JS-only
    // deviation from that fallback.
    qsa(root, '[data-afl-panel-row]').forEach(function (row) {
      row.addEventListener('click', function (e) {
        e.preventDefault();
        var axis = row.getAttribute('data-axis');
        var idx = parseInt(row.getAttribute('data-value'), 10);
        var key = axis === 'price' ? PRICE_KEYS[idx] : SHIPS_KEYS[idx];
        var next = Object.assign({}, ctx.state);
        next[axis] = key;
        closePanel(ctx);
        commit(ctx, next);
      });
    });

    // Apply slab: by the time it's reachable, any row tap has already
    // committed live (above) — this button only needs to close the panel.
    qsa(root, '[data-afl-panel-apply]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closePanel(ctx);
      });
    });

    // Value pill removal + Clear filter are delegated on `root` (not bound to
    // the specific DOM nodes at init) — a Section Rendering fetch swaps the
    // `[data-afl-result]` region wholesale, which would otherwise leave a
    // freshly-swapped-in "Clear filter" link with no listener and fall back
    // to a full page navigation (LOW review fix).
    root.addEventListener('click', function (e) {
      var pill = e.target.closest && e.target.closest('[data-afl-pill]');
      if (pill) {
        e.preventDefault();
        var axis = pill.getAttribute('data-afl-pill');
        var next = Object.assign({}, ctx.state);
        next[axis] = '';
        commit(ctx, next);
        return;
      }
      var clear = e.target.closest && e.target.closest('[data-afl-clear-filter]');
      if (clear) {
        e.preventDefault();
        commit(ctx, { form: '', price: '', ships: '', sort: ctx.state.sort });
      }
    });

    // Sort.
    qsa(root, '[data-afl-sort-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        commit(ctx, Object.assign({}, ctx.state, { sort: select.value }));
      });
    });

    // H1 review fix: mirror afroyla-collection-hub.js's popstate — decide the
    // re-fetch the same diff-based way commit() does (needsFetch(next, prev)),
    // not "never fetch". Without this, Back from a state that DID fetch (e.g.
    // a price change swapped the grid down to 3 real card nodes) left the DOM
    // stuck at that narrower set: the old code only toggled `hidden` on
    // whatever nodes happened to still be present, it never re-fetched the
    // wider grid the restored (unfiltered) URL actually describes.
    window.addEventListener('popstate', function () {
      var next = stateFromSearch(location.search);
      var prev = ctx.state;
      ctx.state = next;
      render(ctx, next);
      if (needsFetch(next, prev, ctx)) fetchAndSwap(ctx, next);
    });
  }

  document.querySelectorAll(ROOT_SEL).forEach(init);

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(ROOT_SEL)) init(node);
          else if (node.querySelector && node.querySelector(ROOT_SEL)) node.querySelectorAll(ROOT_SEL).forEach(init);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
